import React from "react";
import { motion } from "framer-motion";
import { Activity, Wrench, MessageSquare, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { Panel, Spinner, SectionTitle, Button, Tag } from "@/components/ui";

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AgentActivity() {
  const { active, loading } = useBusiness();
  const [feed, setFeed] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!active) return;
    const [reqs, convos] = await Promise.all([
      api.get(`/businesses/${active.id}/requests`),
      api.get(`/businesses/${active.id}/conversations`),
    ]);
    const events = [
      ...reqs.data.map((r) => ({ type: "request", at: r.createdAt, title: r.title,
        sub: `${r.customerName || "Customer"} · +${r.waId}`, priority: r.priority, routed: r.routedToTrello })),
      ...convos.data.map((c) => ({ type: "message", at: c.lastAt, title: c.name || `+${c.waId}`,
        sub: c.lastMessage, dir: c.lastDirection })),
    ].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 60);
    setFeed(events);
  }, [active]);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!active) return <Panel className="p-8 text-center text-sm" style={{ color: "var(--text-2)" }}>Pick a workspace.</Panel>;

  return (
    <div>
      <SectionTitle eyebrow={active.name} title="Agent activity">
        <Button variant="ghost" size="sm" onClick={load} data-testid="activity-refresh"><RefreshCw size={14} /> Refresh</Button>
      </SectionTitle>

      {!feed && <Panel className="p-8 flex justify-center"><Spinner /></Panel>}
      {feed && feed.length === 0 && (
        <Panel className="p-10 text-center" data-testid="activity-empty">
          <Activity size={28} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--text-2)" }}>No activity yet. The agent's actions and conversations will stream here.</p>
        </Panel>
      )}
      {feed && feed.length > 0 && (
        <div className="space-y-px" style={{ background: "var(--border)" }} data-testid="activity-feed">
          {feed.map((e, i) => {
            const Icon = e.type === "request" ? Wrench : MessageSquare;
            const color = e.type === "request" ? "var(--accent-2)" : "var(--accent)";
            return (
              <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.015 }}
                className="p-4 flex items-start gap-3" style={{ background: "var(--s1)" }} data-testid={`activity-${i}`}>
                <Icon size={16} style={{ color, flexShrink: 0, marginTop: 2 }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">
                      {e.type === "request" ? "Routed a request" : (e.dir === "out" ? "Agent replied" : "Customer message")}
                    </span>
                    {e.type === "request" && e.priority === "high" && <Tag tone="warning">high</Tag>}
                    {e.type === "request" && (e.routed ? <Tag tone="success">Trello</Tag> : <Tag>logged</Tag>)}
                  </div>
                  <div className="text-xs mt-0.5 truncate" style={{ color: "var(--text-2)" }}>{e.title}</div>
                  <div className="text-xs mt-0.5 truncate" style={{ color: "var(--text-muted)" }}>{e.sub}</div>
                </div>
                <span className="mono text-[10px] flex-shrink-0" style={{ color: "var(--text-muted)" }}>{fmt(e.at)}</span>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
